import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { LoggedUserService } from './logged-user.service';
import { UserRole, ROLE_HIERARCHY } from '../interfaces/user.interface';

/**
 * Feature-specific permissions configuration.
 * Used by state services to determine API endpoints and UI visibility.
 */
export interface FeaturePermissions {
  /** Whether user can create new items */
  readonly canCreate: boolean;
  /** Whether user can edit existing items */
  readonly canEdit: boolean;
  /** Whether user can delete items */
  readonly canDelete: boolean;
  /** Whether user can upload files/documents */
  readonly canUpload: boolean;
  /** Whether user can download files */
  readonly canDownload: boolean;
  /** Whether user can approve items */
  readonly canApprove: boolean;
  /** Whether user can reject items */
  readonly canReject: boolean;
  /** Whether user can export data */
  readonly canExport: boolean;
  /** Whether to show all statuses (false for consultant = only approved) */
  readonly showAllStatuses: boolean;
  /** Which API endpoint to use */
  readonly apiEndpoint: 'standard' | 'consultant';
}

/**
 * Payslips-specific permissions with additional fields.
 */
export interface PayslipsPermissions extends FeaturePermissions {
  /** Whether to show "Visto HR" column (only for consultant) */
  readonly showViewedByHR: boolean;
}

/**
 * Service responsible for authorization and role-based access control.
 * Provides reactive signals and methods to check user permissions.
 * Also provides feature-specific permissions for differentiated access.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthorizationService {
  private readonly _loggedUserService = inject(LoggedUserService);

  /** Current user as signal (converted from observable) */
  private readonly _user = toSignal(this._loggedUserService.userSubject$);

  /**
   * User's roles as computed signal
   * Returns empty array if user is not loaded
   */
  public readonly userRoles = computed(() => this._user()?.roles ?? []);

  /**
   * Whether user is a Labor Consultant
   */
  public readonly isLaborConsultant = computed(() =>
    this.userRoles().includes(UserRole.LABOR_CONSULTANT),
  );

  /**
   * Whether user is an Admin
   */
  public readonly isAdmin = computed(() => this.userRoles().includes(UserRole.ADMIN));

  /**
   * Whether user is HR
   */
  public readonly isHR = computed(() => this.userRoles().includes(UserRole.HR));

  /**
   * Whether user is a Manager
   */
  public readonly isManager = computed(() => this.userRoles().includes(UserRole.MANAGER));

  /**
   * Whether user is an Employee
   */
  public readonly isEmployee = computed(() => this.userRoles().includes(UserRole.EMPLOYEE));

  /**
   * Whether user is an External Professional
   */
  public readonly isExternalProfessional = computed(() =>
    this.userRoles().includes(UserRole.EXTERNAL_PROFESSIONAL),
  );

  /**
   * The user's highest role based on hierarchy
   */
  public readonly highestRole = computed(() => {
    const roles = this.userRoles();
    if (roles.length === 0) return null;

    return roles.reduce((highest, current) => {
      const currentLevel = ROLE_HIERARCHY[current] || 0;
      const highestLevel = ROLE_HIERARCHY[highest] || 0;
      return currentLevel > highestLevel ? current : highest;
    }, roles[0]);
  });

  /**
   * The user's highest role level (numeric)
   */
  public readonly highestRoleLevel = computed(() => {
    const role = this.highestRole();
    return role ? ROLE_HIERARCHY[role] : 0;
  });

  /**
   * Check if user has a specific role
   * @param role - The role to check
   * @returns True if user has the role
   */
  public hasRole(role: UserRole): boolean {
    return this.userRoles().includes(role);
  }

  /**
   * Check if user has any of the specified roles
   * @param roles - Array of roles to check
   * @returns True if user has at least one of the roles
   */
  public hasAnyRole(roles: UserRole[]): boolean {
    return roles.some((role) => this.hasRole(role));
  }

  /**
   * Check if user has all of the specified roles
   * @param roles - Array of roles to check
   * @returns True if user has all the roles
   */
  public hasAllRoles(roles: UserRole[]): boolean {
    return roles.every((role) => this.hasRole(role));
  }

  /**
   * Check if user has a role with at least the specified level
   * @param minLevel - Minimum role level required
   * @returns True if user's highest role is >= minLevel
   */
  public hasMinRoleLevel(minLevel: number): boolean {
    return this.highestRoleLevel() >= minLevel;
  }

  /**
   * Check if user can access a resource restricted to specific roles
   * Note: ADMIN is treated as equivalent to HR, not as a superuser
   * @param allowedRoles - Roles allowed to access the resource
   * @returns True if user can access
   */
  public canAccess(allowedRoles: UserRole[]): boolean {
    return this.hasAnyRole(allowedRoles);
  }

  // ============================================================
  // Feature-specific permissions
  // ============================================================

  /**
   * Get permissions for expense reports feature.
   * HR/Admin: full CRUD + approve/reject
   * Consultant: view only approved, export only
   * @returns FeaturePermissions for expense reports
   */
  public getExpenseReportsPermissions(): FeaturePermissions {
    const isConsultant = this.isLaborConsultant();

    if (isConsultant) {
      return {
        canCreate: false,
        canEdit: false,
        canDelete: false,
        canUpload: false,
        canDownload: true,
        canApprove: false,
        canReject: false,
        canExport: true,
        showAllStatuses: false, // Only approved
        apiEndpoint: 'consultant',
      };
    }

    // HR/Admin permissions - full access
    return {
      canCreate: true,
      canEdit: true,
      canDelete: true,
      canUpload: true,
      canDownload: true,
      canApprove: true,
      canReject: true,
      canExport: true,
      showAllStatuses: true,
      apiEndpoint: 'standard',
    };
  }

  /**
   * Get permissions for leave requests feature.
   * HR/Admin: full CRUD + approve/reject + bulk approve
   * Consultant: view only approved, export only
   * @returns FeaturePermissions for leave requests
   */
  public getLeaveRequestsPermissions(): FeaturePermissions {
    const isConsultant = this.isLaborConsultant();

    if (isConsultant) {
      return {
        canCreate: false,
        canEdit: false,
        canDelete: false,
        canUpload: false,
        canDownload: false,
        canApprove: false,
        canReject: false,
        canExport: true,
        showAllStatuses: false, // Only approved
        apiEndpoint: 'consultant',
      };
    }

    // HR/Admin permissions - full access
    return {
      canCreate: true,
      canEdit: true,
      canDelete: true,
      canUpload: false,
      canDownload: false,
      canApprove: true,
      canReject: true,
      canExport: true,
      showAllStatuses: true,
      apiEndpoint: 'standard',
    };
  }

  /**
   * Get permissions for payslips feature.
   * HR/Admin: view and download only
   * Consultant: upload payslips/F24, view, download, see "Visto HR" column
   * @returns PayslipsPermissions for payslips
   */
  public getPayslipsPermissions(): PayslipsPermissions {
    const isConsultant = this.isLaborConsultant();

    if (isConsultant) {
      return {
        canCreate: false,
        canEdit: false,
        canDelete: false,
        canUpload: true, // Consultant uploads payslips and F24
        canDownload: true,
        canApprove: false,
        canReject: false,
        canExport: true,
        showAllStatuses: true,
        apiEndpoint: 'consultant',
        showViewedByHR: true, // Consultant sees when HR viewed
      };
    }

    // HR/Admin permissions - view/download only
    return {
      canCreate: false,
      canEdit: false,
      canDelete: false,
      canUpload: false, // HR doesn't upload, consultant does
      canDownload: true,
      canApprove: false,
      canReject: false,
      canExport: true,
      showAllStatuses: true,
      apiEndpoint: 'standard',
      showViewedByHR: false, // HR doesn't need this column
    };
  }

  /**
   * Get permissions for documents feature.
   * Both HR and Consultant have identical access: upload, delete, download.
   * @returns FeaturePermissions for documents
   */
  public getDocumentsPermissions(): FeaturePermissions {
    return {
      canCreate: true,
      canEdit: true,
      canDelete: true,
      canUpload: true,
      canDownload: true,
      canApprove: false,
      canReject: false,
      canExport: true,
      showAllStatuses: true,
      apiEndpoint: 'standard',
    };
  }

  /**
   * Get permissions for employees feature.
   * HR/Admin/Manager: full CRUD
   * Consultant: NO ACCESS (excluded from sidebar, but permissions defined for safety)
   * @returns FeaturePermissions for employees
   */
  public getEmployeesPermissions(): FeaturePermissions {
    const isConsultant = this.isLaborConsultant();

    if (isConsultant) {
      // Consultant should not access employees section at all
      // These permissions are defensive - sidebar already hides the menu item
      return {
        canCreate: false,
        canEdit: false,
        canDelete: false,
        canUpload: false,
        canDownload: false,
        canApprove: false,
        canReject: false,
        canExport: false,
        showAllStatuses: false,
        apiEndpoint: 'consultant',
      };
    }

    // HR/Admin/Manager permissions - full access
    return {
      canCreate: true,
      canEdit: true,
      canDelete: true,
      canUpload: false,
      canDownload: false,
      canApprove: false,
      canReject: false,
      canExport: true,
      showAllStatuses: true,
      apiEndpoint: 'standard',
    };
  }
}
